/**
 * Every action Claude can take in the editor, one row each. A row's lane is read here and nowhere else, so moving an
 * action between lanes is a change to its row. Ids are stable, because anything that names an action, such as a permission list, relies on them.
 *
 * What a row doesn't hold is how the action runs: see `ActionBehaviours` in tools/do-action.tool.ts.
 * Pure, with no Angular in reach, so the desktop app's scripted checks load this very file.
 *
 * Signing out has no row, because it would cut Claude's own connection. Modules, AI Query and the internal OOTB screen
 * have none because they are hidden from builders.
 */

export type Lane = 'fast' | 'confirm';

/** A screen as `navigate` names it, or a row that isn't tied to one. */
export type ActionScreen =
  | 'select-project' | 'data' | 'entity' | 'pages' | 'page-preview' | 'page-spotlight' | 'page-data' | 'function-editor'
  | 'team' | 'workspaces' | 'bastion' | 'chart-style' | 'settings' | 'everywhere';

export type FieldType = 'string' | 'number' | 'boolean' | 'array' | 'object';

export interface ActionField {
  name: string;
  type: FieldType;
  /** Write-only. Reads and answers give only set or not set, and usage records drop it. */
  secret?: boolean;
  /** The screen shows it and Claude can't write it: a list, a generated password, a file the builder picks. */
  readOnly?: boolean;
  /** Says which one, or what to change it to. The screen holds no such value, so a read has nothing to give for it. */
  picks?: boolean;
}

export type ActionCommit =
  /** read_screen or another tool already does this; `do_action` points there. */
  | { kind: 'own-tool'; tool: string }
  /** Runs what the screen's button runs. */
  | { kind: 'runs' }
  /** The screen has its own button, and the builder presses it. */
  | { kind: 'press'; button: string }
  /** One click with no form: the builder confirms on an overlay. */
  | { kind: 'overlay' };

export interface ActionRow {
  id: string;
  label: string;
  screen: ActionScreen;
  /** The tab, panel or Settings card it lives on, as `navigate` names it. */
  panel?: string;
  lane: Lane;
  fields: ActionField[];
  commit: ActionCommit;
  /** Builder-level: it needs no project open. */
  worksWithoutProject?: boolean;
  /** Said instead of the plain not-built answer, where Claude can point the builder somewhere better. */
  whyNotBuilt?: string;
}

const text = (name: string): ActionField => ({ name, type: 'string' });
const flag = (name: string): ActionField => ({ name, type: 'boolean' });
const count = (name: string): ActionField => ({ name, type: 'number' });
const list = (name: string): ActionField => ({ name, type: 'array' });
const pick = (name: string, type: FieldType = 'string'): ActionField => ({ name, type, picks: true });
const shown = (name: string, type: FieldType = 'array'): ActionField => ({ name, type, readOnly: true });
const secret = (name: string): ActionField => ({ name, type: 'string', secret: true });
const secretShown = (name: string): ActionField => ({ name, type: 'string', secret: true, readOnly: true });

const runs: ActionCommit = { kind: 'runs' };
const overlay: ActionCommit = { kind: 'overlay' };
const press = (button: string): ActionCommit => ({ kind: 'press', button });

const reading = (id: string, label: string, screen: ActionScreen, fields: ActionField[], worksWithoutProject = false): ActionRow => ({
  id, label, screen, lane: 'fast', fields, commit: { kind: 'own-tool', tool: 'read_screen' },
  ...(worksWithoutProject ? { worksWithoutProject } : {}),
});
const fast = (id: string, label: string, screen: ActionScreen, fields: ActionField[], more: Partial<ActionRow> = {}): ActionRow => ({
  id, label, screen, lane: 'fast', fields, commit: runs, ...more,
});
const confirm = (id: string, label: string, screen: ActionScreen, fields: ActionField[], commit: ActionCommit, more: Partial<ActionRow> = {}): ActionRow => ({
  id, label, screen, lane: 'confirm', fields, commit, ...more,
});

const workspace = text('workspaceId');
const person = text('personId');

export const ACTION_TABLE: readonly ActionRow[] = [
  // ---- Fast lane: reading anything except secrets ----
  reading('read-select-project', 'Read the project list', 'select-project', [shown('projects')], true),
  reading('read-data', 'Read the entity list', 'data', [shown('entities'), shown('graphEngine', 'string')]),
  reading('read-entity', 'Read the open entity', 'entity', [shown('entity', 'object'), shown('unsavedChanges', 'number')]),
  reading('read-pages', 'Read the pages list', 'pages', [shown('pages')]),
  reading('read-page-preview', 'Read the page in Preview', 'page-preview', []),
  reading('read-page-spotlight', 'Read the page in Spotlight', 'page-spotlight', []),
  reading('read-page-data', 'Read the page\'s data', 'page-data', [shown('pageEntities'), shown('entityChoices'), shown('variables', 'object')]),
  reading('read-function-editor', 'Read the function editor', 'function-editor', []),
  reading('read-team', 'Read the team', 'team', [shown('members'), shown('appRoles')]),
  reading('read-workspaces', 'Read the workspaces', 'workspaces', [shown('workspaces'), shown('people'), shown('roles'), shown('projectSwitches', 'object'), shown('invitations')]),
  reading('read-bastion', 'Read Bastion', 'bastion', []),
  // Was (2026-10-30): reading('read-schema', 'Read the schema', 'schema', [shown('tables'), shown('selectedTable', 'string')]),
  // Claude doesn't reach Schema explorer.
  reading('read-chart-style', 'Read the chart style', 'chart-style', [shown('presets'), shown('knobs')], true),
  reading('read-settings', 'Read Settings', 'settings', [
    // Was (2026-10-30), before a builder code counted as a secret: shown('builderCode', 'string'),
    secretShown('builderCode'), shown('tenants'), shown('selectedTenant', 'string'), shown('publishUrl', 'string'), shown('devUrl', 'string'),
    shown('publishVersion', 'string'), shown('versions'), shown('appBars', 'object'), shown('developerMode', 'boolean'), shown('worksOffline', 'boolean'),
    shown('devApk', 'object'), shown('liveApk', 'object'), shown('apple', 'object'), shown('iphoneBuild', 'object'), shown('iphonePublicLink', 'object'),
    shown('appStoreReminders', 'object'), shown('listingErrors'), shown('checklist'), shown('canSubmit', 'boolean'), shown('submission', 'object'),
    shown('privacyChoice', 'string'), shown('privacyApproved', 'boolean'), shown('screenshots'), shown('testLoginRoles'),
  ]),

  // ---- Fast lane: moving ----
  { id: 'open-project', label: 'Open a project', screen: 'everywhere', lane: 'fast', fields: [text('project')], commit: { kind: 'own-tool', tool: 'open_project' }, worksWithoutProject: true },
  { id: 'go-to-screen', label: 'Go to a screen, a tab, or a page in Preview or Spotlight', screen: 'everywhere', lane: 'fast', fields: [text('screen'), text('id'), text('panel')], commit: { kind: 'own-tool', tool: 'navigate' }, worksWithoutProject: true },
  fast('search-projects', 'Search projects', 'select-project', [text('searchTerm')], { worksWithoutProject: true }),
  fast('search-pages', 'Search pages', 'pages', [text('searchTerm')]),
  fast('search-workspaces', 'Search workspaces and people', 'workspaces', [text('searchTerm')]),
  fast('filter-pages', 'Filter pages', 'pages', [text('filter')]),
  // Was (2026-10-30), until a real run showed the Data screen draws neither the filter nor its list: fast('filter-entities', 'Filter entities', 'data', [text('filter')]),
  fast('filter-entities', 'Filter entities', 'data', [text('filter')], {
    whyNotBuilt: 'Claude can\'t filter the entities: the Data screen doesn\'t show a filtered list, so the builder would see no change.',
  }),
  fast('expand-workspace-row', 'Expand a workspace row', 'workspaces', [workspace]),
  fast('reload-list', 'Reload the list on this screen', 'everywhere', [], { worksWithoutProject: true }),
  fast('pick-tenant', 'Pick a tenant', 'settings', [pick('tenant')], { panel: 'data-tenant' }),
  fast('toggle-graph-engine', 'Switch the graph engine', 'data', [text('graphEngine')]),

  // ---- Fast lane: saved text and small settings ----
  fast('rename-workspace', 'Rename a workspace', 'workspaces', [workspace, text('name')]),
  fast('rename-role', 'Rename a role', 'workspaces', [text('roleId'), text('name')], { panel: 'roles' }),
  fast('set-person-name', 'Change a person\'s name', 'workspaces', [workspace, person, text('name')]),
  fast('set-mobile-app-name', 'Set the mobile app name', 'settings', [text('mobileAppName')], { panel: 'mobile-app' }),
  fast('set-app-bars', 'Set the app bars', 'settings', [pick('bar'), pick('colour')], { panel: 'phone-bars' }),
  fast('set-chart-style', 'Set the chart style', 'chart-style', [text('scope'), { name: 'theme', type: 'object' }], { worksWithoutProject: true }),
  fast('save-app-review-details', 'Save the app review details', 'settings', [
    text('contactFirstName'), text('contactLastName'), text('contactEmail'), text('contactPhone'), text('demoUsername'), secret('demoPassword'), text('notes'),
  ], { panel: 'iphone-app' }),
  fast('save-app-store-listing', 'Save the App Store listing draft', 'settings', [
    text('listingName'), text('subtitle'), text('description'), text('keywords'), text('supportUrl'), text('marketingUrl'), text('promotionalText'),
    text('copyright'), text('primaryCategory'), text('secondaryCategory'), flag('noObjectionableContent'), flag('usesThirdPartyContent'),
    flag('traderStatusConfirmed'), text('countries'), text('distribution'),
  ], { panel: 'iphone-app' }),
  fast('save-apple-rejection-notes', 'Save Apple\'s rejection notes', 'settings', [text('rejectionNotes')], { panel: 'iphone-app' }),
  fast('save-privacy-policy-draft', 'Save the draft privacy policy', 'settings', [text('privacyText')], { panel: 'iphone-app' }),

  // ---- Fast lane: creating easily undone things ----
  fast('create-page', 'Create a page', 'pages', [text('newPageName'), text('newPageType')], { panel: 'new-page' }),
  fast('create-role', 'Create a role', 'workspaces', [text('newRoleName')], { panel: 'roles' }),
  fast('create-workspace', 'Create a workspace', 'workspaces', [text('newWorkspaceName')]),

  // ---- Fast lane: Page Data ----
  fast('add-page-entity', 'Add an entity to the page', 'page-data', [text('selectedNewEntity')]),
  fast('add-page-variable', 'Add a variable to the page', 'page-data', [text('variableName'), text('variableType'), { name: 'variableValue', type: 'string' }], { panel: 'add-variable' }),
  fast('save-page-data', 'Save the page\'s data', 'page-data', []),

  // ---- Fast lane: switches ----
  fast('set-workspace-arrival-notices', 'Switch workspace arrival notices', 'workspaces', [workspace, flag('enabled')]),
  fast('set-developer-mode', 'Switch Developer mode', 'settings', [pick('enabled', 'boolean')], { panel: 'preview' }),
  fast('tick-website-checklist', 'Tick a website checklist item', 'settings', [pick('item'), pick('ticked', 'boolean')], { panel: 'iphone-app' }),

  // ---- Fast lane: small jobs ----
  fast('build-dev-apk', 'Build the Dev APK', 'settings', [], { panel: 'preview' }),
  fast('refresh-apple-status', 'Refresh the Apple status', 'settings', [], { panel: 'iphone-app' }),
  // Was (2026-10-30), before Pages offered it: on 'settings' with whyNotBuilt sending the builder to the button on Pages.
  fast('dismiss-failed-task', 'Dismiss a failed task', 'pages', [pick('taskId'), shown('failedTasks')]),

  // ---- Confirm lane: entities ----
  confirm('create-entity', 'Create an entity', 'data', [text('entityName'), text('entityKind')], press('Save')),
  confirm('rename-entity', 'Name a new entity', 'entity', [text('entityName')], press('Save')),
  confirm('save-entity', 'Save the entity', 'entity', [text('entityName')], press('Save')),
  confirm('update-entity', 'Update the entity', 'entity', [], press('Update')),
  confirm('finalize-entity', 'Finalize the entity', 'entity', [], press('Finalize')),
  confirm('edit-entity-field', 'Edit a field', 'entity', [text('fieldName'), text('newName'), text('fieldType'), text('description')], press('Save')),
  confirm('delete-entity-field', 'Delete a field', 'entity', [text('fieldName')], press('Save')),
  confirm('set-predefined-rows', 'Set the predefined rows', 'entity', [list('rows')], press('Save'), { panel: 'predefined-rows' }),
  confirm('set-role-permissions', 'Set the role permissions', 'entity', [list('rolePermissions')], press('Save Permissions'), { panel: 'role-permissions' }),
  confirm('sync-entities', 'Sync entities', 'data', [], press('Sync')),
  confirm('import-entities', 'Import an entity', 'data', [text('importId')], press('Import')),

  // ---- Confirm lane: people ----
  confirm('set-seat-cap', 'Set a workspace\'s seat cap', 'workspaces', [workspace, count('seatCap'), flag('unlimited')], press('Save')),
  confirm('add-person', 'Add a person to a workspace', 'workspaces', [
    workspace, text('mode'), text('email'), text('username'), secret('password'), text('roleId'), secretShown('generatedPassword'),
  ], press('Send invitation')),
  confirm('team-add-member', 'Add someone to the team', 'team', [secret('code'), text('level')], press('Add to project'), { panel: 'add-member' }),
  confirm('change-person-role', 'Change a person\'s role', 'workspaces', [workspace, person, text('roleId')], overlay),
  confirm('change-app-role', 'Change a team member\'s app role', 'team', [text('memberId'), text('appRoleId')], overlay),
  confirm('change-team-level', 'Change a team member\'s level', 'team', [text('memberId'), text('level')], overlay),
  confirm('set-person-password', 'Set a person\'s password', 'workspaces', [workspace, person, secret('password'), secretShown('generatedPassword')], press('Set password')),
  confirm('set-person-sign-in-email', 'Add a sign-in email for a person who has none', 'workspaces', [workspace, person, text('email')], press('Send link')),
  confirm('remove-person', 'Remove a person from a workspace', 'workspaces', [workspace, person], press('Remove')),
  confirm('team-remove-member', 'Remove someone from the team', 'team', [text('memberId')], overlay),
  confirm('transfer-person', 'Transfer a person\'s work to someone else in the workspace', 'workspaces', [workspace, person, text('toPersonId'), list('entityIds')], press('Transfer')),
  confirm('erase-person', 'Erase a person', 'workspaces', [workspace, person, text('confirmText')], press('Erase')),
  confirm('reinstate-person', 'Reinstate a person', 'workspaces', [workspace, person, text('roleId'), secret('password')], press('Reinstate')),
  confirm('restore-workspace', 'Restore a workspace being deleted', 'workspaces', [workspace], press('Restore')),

  // ---- Confirm lane: reaches people outside the builder ----
  confirm('set-workspace-invites', 'Switch a workspace\'s invites', 'workspaces', [workspace, flag('enabled')], overlay),
  confirm('set-workspace-adds', 'Switch a workspace\'s adds', 'workspaces', [workspace, flag('enabled')], overlay),
  confirm('set-project-invites', 'Switch the project\'s invites', 'workspaces', [flag('enabled')], overlay),
  confirm('set-project-signups', 'Switch the project\'s signups', 'workspaces', [flag('enabled')], overlay),
  confirm('set-mobile-access-mode', 'Set the mobile access mode', 'workspaces', [text('mode')], overlay),
  confirm('resend-invite', 'Resend an invite', 'workspaces', [workspace, text('inviteId')], overlay),
  confirm('cancel-invite', 'Cancel an invite', 'workspaces', [workspace, text('inviteId')], overlay),
  confirm('iphone-public-link', 'Switch the TestFlight link', 'settings', [pick('enabled', 'boolean')], overlay, { panel: 'iphone-app' }),

  // ---- Confirm lane: live app ----
  // The card takes no note: publishNote is what the last publish said back.
  confirm('publish', 'Publish the app', 'settings', [shown('publishNote', 'string')], overlay, { panel: 'publish' }),
  confirm('revert-version', 'Revert to an earlier version', 'settings', [pick('version')], overlay, { panel: 'publish' }),
  confirm('unpublish', 'Unpublish the app', 'settings', [text('unpublishName')], press('Unpublish'), { panel: 'publish' }),
  confirm('rebuild', 'Rebuild the app', 'settings', [text('rebuildName'), text('rebuildNote')], press('Confirm'), { panel: 'iphone-app' }),
  confirm('set-works-offline', 'Switch Works offline', 'settings', [pick('enabled', 'boolean')], overlay, { panel: 'works-offline' }),
  confirm('set-time-zone', 'Set the time zone', 'settings', [text('timeZone'), shown('timeZonePending', 'string')], press('Change time zone'), { panel: 'time-zone' }),
  confirm('build-live-apk', 'Build the Live APK', 'settings', [], overlay, { panel: 'publish' }),
  confirm('build-iphone', 'Build for iPhone', 'settings', [], overlay, { panel: 'iphone-app' }),
  confirm('iphone-nudge', 'Switch App Store reminders', 'settings', [pick('enabled', 'boolean')], overlay, { panel: 'iphone-app' }),

  // ---- Confirm lane: Apple ----
  // The .p8 is a file only the builder can pick, so it reads as set or not set and takes no write.
  confirm('connect-apple', 'Connect Apple', 'settings', [secret('teamId'), secret('issuerId'), secret('keyId'), secretShown('p8')], press('Connect Apple account'), { panel: 'iphone-app' }),
  confirm('approve-privacy-policy', 'Approve the privacy policy', 'settings', [], overlay, { panel: 'iphone-app' }),
  confirm('submit-to-app-store', 'Submit to the App Store', 'settings', [], overlay, { panel: 'iphone-app' }),
  confirm('remove-screenshot', 'Remove a screenshot', 'settings', [pick('screenshotId')], overlay, { panel: 'iphone-app' }),

  // ---- Confirm lane: secrets and logins ----
  confirm('set-runtime-test-password', 'Set the runtime test password', 'settings', [secretShown('runtimeLoginEmail'), secretShown('runtimeLoginPassword'), secret('runtimeNewPassword')], press('Update'), { panel: 'live-login' }),
  confirm('rotate-test-login', 'Rotate a test login', 'settings', [pick('roleId'), secretShown('testLogins')], overlay, { panel: 'preview' }),

  // ---- Confirm lane: other ----
  confirm('create-project', 'Create a project', 'select-project', [text('newProjectName')], press('Create'), { panel: 'create-project', worksWithoutProject: true }),
  confirm('clone-project', 'Clone a project', 'select-project', [text('project'), text('cloneName')], press('Clone'), { worksWithoutProject: true }),
  confirm('delete-page', 'Delete a page', 'pages', [text('pageId'), text('deleteConfirmText')], press('Delete Page')),
];

/** Every secret field's name, across the table. */
export function secretFieldNames(table: readonly ActionRow[]): string[] {
  const names = new Set<string>();
  for (const row of table) for (const field of row.fields) if (field.secret) names.add(field.name);
  return [...names];
}
